import { BellOutlined, PushpinOutlined } from "@ant-design/icons";
import { TaskInfoTypeIcon, UpgradePremiumIcon } from "@assets/icons";
import {
  CardTitle,
  KButton,
  KFormUser,
  KMdText,
  KModalAddCard,
  KSmTextSecondary50,
} from "@components";
import {
  ECardType,
  EToDoRepeatStatus,
  ICombinedCard,
  IUserAccessProfile,
} from "@types";
import { Flex } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { SetRepeat } from "./ToDoDetail/SetRepeat";
import { UserAccessChecklist } from "./ToDoDetail/UserAccessChecklist";
import { usePremiumPlan } from "@hooks";

const MAX_REMINDER_TITLE_LENGTH = 99;

export const SetReminderSection = () => {
  const { getValues, setValue, control, watch } = useFormContext();
  const { append } = useFieldArray({
    control,
    name: "reminder.specifiedRecipients",
  });
  const isPremium = usePremiumPlan();
  const [isSetReminder, setIsSetReminder] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const recipients = useMemo(
    () => getValues("reminder.specifiedRecipients"),
    [watch("reminder.specifiedRecipients")]
  );

  const remainingTitleLength = useMemo(
    () => MAX_REMINDER_TITLE_LENGTH - (watch("reminder.title")?.length ?? 0),
    [watch("reminder.title")]
  );

  const remainingNotificationMessageLength = useMemo(
    () =>
      MAX_REMINDER_TITLE_LENGTH -
      (watch("reminder.notificationMessage")?.length ?? 0),
    [watch("reminder.notificationMessage")]
  );

  const handleOpenModal = () => {
    setShowModal(true);
  };

  useEffect(() => {
    setIsSetReminder(getValues("isSetReminder"));
    if (getValues("isSetReminder") && !getValues("reminder.repeat")) {
      setValue("reminder.repeat", EToDoRepeatStatus.NEVER);
    }
  }, [watch("isSetReminder")]);

  const handleCheckRecipient = (
    e: CheckboxChangeEvent,
    user: IUserAccessProfile
  ) => {
    const { checked } = e.target;
    const { user: _id } = user;
    if (checked) {
      append(_id);
    } else {
      const newCheckList = getValues("reminder.specifiedRecipients").filter(
        (u: string) => u !== _id
      );
      setValue("reminder.specifiedRecipients", newCheckList, {
        shouldDirty: true,
      });
    }
  };

  return (
    <>
      <KFormUser.Switch
        icon={<PushpinOutlined />}
        title="Pin to Upcoming"
        name="isFollowedUp"
      />
      <KFormUser.Switch
        icon={<BellOutlined />}
        title="Set a Reminder"
        name="isSetReminder"
      />
      {isSetReminder && (
        <Flex gap={16} vertical style={{ position: "relative" }}>
          <KFormUser.Input
            maxLength={MAX_REMINDER_TITLE_LENGTH}
            name="reminder.title"
            label={
              <Flex flex={1} justify="space-between">
                <KMdText>Reminder Title</KMdText>
                <KSmTextSecondary50>
                  {remainingTitleLength}/{MAX_REMINDER_TITLE_LENGTH}
                </KSmTextSecondary50>
              </Flex>
            }
            placeholder="Enter reminder title"
          />
          <SetRepeat time="reminder.startTime" repeat="reminder.repeat" />
          <KFormUser.Switch
            label="Only certain people"
            name="reminder.isSpecifiedRecipients"
          />
          <UserAccessChecklist
            ids={recipients}
            onCheck={handleCheckRecipient}
          />
          <KFormUser.Input
            maxLength={MAX_REMINDER_TITLE_LENGTH}
            name="reminder.notificationMessage"
            label={
              <Flex flex={1} justify="space-between">
                <KMdText>Text to show on Push Notification</KMdText>
                <KSmTextSecondary50>
                  {remainingNotificationMessageLength}/
                  {MAX_REMINDER_TITLE_LENGTH}
                </KSmTextSecondary50>
              </Flex>
            }
            placeholder="Text to show on Push Notification"
          />
        </Flex>
      )}
      <CardTitle
        icon={<TaskInfoTypeIcon width={17} />}
        children="Create a linked To-Do"
        suffix={
          !isPremium ? (
            <UpgradePremiumIcon />
          ) : (
            <KButton
              onClick={handleOpenModal}
              title="Add To-Do"
              size="small"
              ghost
            />
          )
        }
      />
      <KModalAddCard
        cardType={ECardType.ToDos}
        linkedId={getValues()._id}
        onOpen={setShowModal}
        open={showModal}
      />
    </>
  );
};
